import { useEffect, useState, useRef, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Input } from "@workspace/web-ui/components/input";
import { Label } from "@workspace/web-ui/components/label";

import { WelcomeFormSchema } from "../schema";
import { InputField } from "../types";
import { FormContainer, WelcomeStepFormProps } from "./form-container";

export function TimeFrameStep({ data, updateFields }: WelcomeStepFormProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WelcomeFormSchema>();

  const targetWeightValue = watch("targetWeight");
  const timeframeValue = watch("timeframe");
  
  // Dropdown state for timeframe
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout>(null);

  // Auto-close dropdown after 3 seconds of inactivity
  useEffect(() => {
    if (dropdownOpen) {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      dropdownTimeoutRef.current = setTimeout(() => {
        setDropdownOpen(false);
      }, 5000);
    }
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [dropdownOpen, timeframeValue]);

  // Generate options based on input
  const timeframeOptions = useMemo(() => {
    const num = parseInt(timeframeValue, 10);
    if (!timeframeValue || isNaN(num) || num <= 0) return [];
    if (num > 4) {
      return [{ label: `${num} months`, value: `${num} months` }];
    }
    return [
      { label: `${num} weeks`, value: `${num} weeks` },
      { label: `${num} months`, value: `${num} months` },
    ];
  }, [timeframeValue]);

  // Debug log
  console.log('timeframeValue:', timeframeValue, 'timeframeOptions:', timeframeOptions, 'dropdownOpen:', dropdownOpen);

  useEffect(() => {
    updateFields({
      targetWeight: targetWeightValue,
      timeframe: timeframeValue,
    });
  }, [targetWeightValue, timeframeValue, updateFields]);

  // Open dropdown when valid number is typed
  useEffect(() => {
    const num = parseInt(timeframeValue, 10);
    if (timeframeValue && !isNaN(num) && num > 0) {
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
    }
  }, [timeframeValue]);

  return (
    <FormContainer heading={data.heading} description={data.description}>
      {data.form &&
        (data.form as InputField[]).map((form) => {
          const name = form.name as keyof WelcomeFormSchema;

          const { label, type, placeholder } = form;
          if (name === "targetWeight") {
            return (
                <div className="w-full space-y-2" key={label}>
                  <Label
                    htmlFor={label}
                    className="mb-4 flex self-start text-left text-lg text-[#7F8493] transition-colors duration-200 ease-in-out peer-checked:text-[#363840]"
                  >
                    {label}
                  </Label>
                  <div className="flex w-full rounded-2xl shadow-sm shadow-black/5">
                    <Input
                      id={label}
                      placeholder={placeholder}
                      type={type}
                      className="peer -me-px h-[40px] w-11/12 rounded-s-2xl rounded-e-none p-4 shadow-none sm:h-[60px] lg:h-[80px] lg:w-11/12"
                      {...register(name as keyof WelcomeFormSchema)}
                    />
                    {name === "targetWeight" && (
                      <span className="z-10 inline-flex items-center rounded-e-2xl border border-input px-3 text-sm text-[#363840]">
                        Lbs
                      </span>
                    )}
                  </div>
                  {name === "targetWeight" && errors.targetWeight && (
                    <p className="text-sm text-[#7F8493]">
                      <p className="text-sm text-red-500">
                        {errors.targetWeight.message}
                      </p>
                    </p>
                  )}
                </div>
              );
          }

          // Timeframe input with dropdown
          if (name === "timeframe") {
            return (
              <div className="w-full space-y-2" key={label}>
                <Label
                  htmlFor={label}
                  className="mb-4 flex self-start w-full text-left text-lg text-[#7F8493] transition-colors duration-200 ease-in-out peer-checked:text-[#363840]"
                >
                  {label}
                </Label>
                <div className="relative flex w-full rounded-2xl shadow-md border-2 border-[#F1F1F1] shadow-black/5">
                  <Input
                    id={label}
                    placeholder={placeholder}
                    type={type}
                    className="peer border-none focus-visible:ring-0 h-[40px] w-11/12 rounded-s-2xl rounded-e-none p-4 shadow-none sm:h-[60px] lg:h-[80px] lg:w-11/12"
                    value={timeframeValue}
                    ref={inputRef}
                    autoComplete="off"
                    onChange={e => {
                      setValue("timeframe", e.target.value, { shouldValidate: true, shouldDirty: true });
                      const num = parseInt(e.target.value, 10);
                      if (e.target.value && !isNaN(num) && num > 0) setDropdownOpen(true);
                      else setDropdownOpen(false);
                    }}
                    onFocus={() => {
                      const num = parseInt(timeframeValue, 10);
                      if (timeframeValue && !isNaN(num) && num > 0) setDropdownOpen(true);
                    }}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                  />
                  {dropdownOpen && timeframeOptions.length > 0 && (
                    <div className="absolute overflow-auto hide-scrollbar h-[100px] left-0 top-[100%] mt-1 w-full min-w-[180px] p-1 bg-white border rounded-lg shadow">
                      {timeframeOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="cursor-pointer flex px-2 py-1 hover:bg-gray-100"
                          onMouseDown={() => {
                            setValue("timeframe", opt.value, { shouldValidate: true, shouldDirty: true });
                            setDropdownOpen(false);
                          }}
                        >
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="z-10 inline-flex w-[72px] items-center justify-center border-l-2 border-[#F1F1F1] rounded-e-2xl bg-white px-3 text-sm text-[#363840]">
                    Time
                  </span>
                </div>
                {errors.timeframe && (
                  <p className="text-sm text-red-500">
                    {errors.timeframe.message}
                  </p>
                )}
              </div>
            );
          }

          return (
            <div className="w-full space-y-2" key={label}>
              <Label
                htmlFor={label}
                className="mb-4 flex self-start w-full text-left text-lg text-[#7F8493] transition-colors duration-200 ease-in-out peer-checked:text-[#363840]"
              >
                {label}
              </Label>
              <div className="flex w-full rounded-lg shadow-sm shadow-black/5">
                <Input
                  id={label}
                  placeholder={placeholder}
                  type={type}
                  className="peer -me-px h-[40px] w-full rounded-2xl p-4 shadow-none sm:h-[60px] lg:h-[80px] lg:w-full"
                  {...register(name as keyof WelcomeFormSchema)}
                />
              </div>
            </div>
          );


        })}
    </FormContainer>
  );
}
